import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useDisclosure } from 'hooks'
import { notifications, useCurrentUser } from 'context'
import { IconX } from '@tabler/icons-react'
import { v4 as uuid } from 'uuid'
import type {
  AddStudentsTab,
  Group,
  InviteCodeData,
  StudentFormRow,
  StudentRowErrors,
  BatchCreateResponse,
} from '../components/AddStudentsModal/interfaces'
import {
  getGroups,
  getSchoolGroups,
  getGrades,
  getTutorInviteCode,
  createStudentsBatch,
} from '../service/addStudentsService'
import { createGroupQuick } from '../service/studentRequestsService'
import {
  removeSubmittedRows,
  resolveFailedRowIds,
  collectSubmittedGroupIds,
} from './removeSubmittedRows'

function sanitizeForUsername(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_.]/g, '')
}

function deriveUsername(first: string, last: string): string {
  if (!first.trim() && !last.trim()) return ''
  const f = sanitizeForUsername(first.trim())
  // Multiple last names are allowed; each one composes the username,
  // joined by dots (e.g. "Silva Santos" -> "silva.santos").
  const l = last
    .trim()
    .split(/\s+/)
    .map(sanitizeForUsername)
    .filter(Boolean)
    .join('.')
  if (!f) return l
  if (!l) return f
  return `${f}.${l}`.slice(0, 25)
}

const USERNAME_REGEX = /^[a-zA-Z0-9_.]+$/
const USERNAME_MIN_LENGTH = 3
const USERNAME_MAX_LENGTH = 25
const hasValidUsernameLength = (u: string): boolean =>
  u.length >= USERNAME_MIN_LENGTH && u.length <= USERNAME_MAX_LENGTH

const createEmptyStudent = (): StudentFormRow => ({
  id: uuid(),
  firstName: '',
  lastName: '',
  username: '',
  usernameEdited: false,
  gradeId: null,
  groupIds: [],
})

const hasData = (s: StudentFormRow): boolean =>
  Boolean(s.firstName.trim() || s.lastName.trim())

interface ContextType {
  opened: boolean
  activeTab: AddStudentsTab
  groups: Group[]
  grades: Array<{ id: string; name: string }>
  students: StudentFormRow[]
  inviteCode: InviteCodeData | null
  loading: boolean
  loadingGroups: boolean
  savingStudents: boolean
  hasUnsavedChanges: boolean
  showDiscardModal: boolean
  submitResult: BatchCreateResponse | null
  submittedGroupIds: string[]
  canFillDownGrade: boolean
  canFillDownGroup: boolean

  open: (preselectedGroupId?: string) => Promise<void>
  close: () => void
  setActiveTab: (tab: AddStudentsTab) => void
  updateStudent: (
    index: number,
    field: 'firstName' | 'lastName' | 'username' | 'gradeId',
    value: string
  ) => void
  updateStudentGroupIds: (index: number, value: string[]) => void
  removeStudent: (index: number) => void
  addStudent: () => void
  submitStudents: () => Promise<void>
  setShowDiscardModal: (show: boolean) => void
  confirmDiscard: () => void
  clearSubmitResult: () => void
  fillDownGrade: () => void
  fillDownGroup: () => void
  dismissSuccess: () => void
  createGroup: (name: string) => Promise<void>
}

const Ctx = createContext<ContextType | undefined>(undefined)

interface ProviderProps {
  children: ReactNode
  onStudentsAdded?: () => void
  onGroupCreated?: () => void
}

export function AddStudentsModalProvider({
  children,
  onStudentsAdded,
  onGroupCreated,
}: ProviderProps) {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()
  const isTutor = currentUser?.profile_used === 'tutor'
  const [opened, { open: openModal, close: closeModal }] =
    useDisclosure(false)

  const [activeTab, setActiveTab] =
    useState<AddStudentsTab>('invite-code')
  const [groups, setGroups] = useState<Group[]>([])
  const [grades, setGrades] =
    useState<Array<{ id: string; name: string }>>([])
  const [students, setStudents] =
    useState<StudentFormRow[]>([createEmptyStudent()])
  const [inviteCode, setInviteCode] =
    useState<InviteCodeData | null>(null)
  const [submitResult, setSubmitResult] =
    useState<BatchCreateResponse | null>(null)
  // Snapshot of the group ids sent in the last batch — the success screen
  // reads groups from here because the form rows are cleared after a
  // successful submit (AR-783).
  const [submittedGroupIds, setSubmittedGroupIds] =
    useState<string[]>([])

  const [loading, setLoading] = useState(false)
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [savingStudents, setSavingStudents] = useState(false)
  const [showDiscardModal, setShowDiscardModal] =
    useState(false)

  const hasUnsavedChanges = students.some(hasData)

  const firstWithData = students.find(hasData)
  const canFillDownGrade = !!firstWithData?.gradeId
  const canFillDownGroup = !!firstWithData && firstWithData.groupIds.length > 0

  const resetState = useCallback(() => {
    setActiveTab('invite-code')
    setStudents([createEmptyStudent()])
    setInviteCode(null)
    setSubmitResult(null)
    setSubmittedGroupIds([])
    setLoading(false)
  }, [])

  const open = useCallback(
    async (preselectedGroupId?: string) => {
      resetState()
      setLoadingGroups(true)
      setLoading(true)
      openModal()

      // my_groups (/api/tutors/my_groups) é tutor-only; para os demais
      // perfis (ex.: manager) buscamos as turmas da escola via
      // /api/arc_mosaico/groups — antes o manager abria o modal com o
      // dropdown de turmas vazio e não conseguia adicionar alunos (AR-735).
      const [groupsRes, inviteRes, gradesRes] =
        await Promise.allSettled([
          isTutor ? getGroups() : getSchoolGroups(),
          getTutorInviteCode(),
          getGrades(),
        ])

      if (groupsRes.status === 'fulfilled') {
        setGroups(groupsRes.value)
        if (preselectedGroupId) {
          setStudents(prev =>
            prev.map(s => ({ ...s, groupIds: [preselectedGroupId] })),
          )
        }
      } else {
        notifications.show({
          message: t('add_students_modal.error_loading_groups'),
          color: 'red',
          icon: <IconX size={24} />,
        })
      }

      if (inviteRes.status === 'fulfilled') {
        setInviteCode(inviteRes.value)
      }

      if (gradesRes.status === 'fulfilled') {
        setGrades(gradesRes.value)
      }

      setLoadingGroups(false)
      setLoading(false)
    },
    [resetState, openModal, t, isTutor],
  )

  const close = useCallback(() => {
    if (submitResult && submitResult.failedStudents.length === 0 && submitResult.addedStudents.length > 0) {
      closeModal()
      resetState()
      return
    }
    if (hasUnsavedChanges) {
      setShowDiscardModal(true)
      return
    }
    closeModal()
    resetState()
  }, [hasUnsavedChanges, submitResult, resetState, closeModal])

  const confirmDiscard = useCallback(() => {
    setShowDiscardModal(false)
    closeModal()
    resetState()
  }, [resetState, closeModal])

  const dismissSuccess = useCallback(() => {
    setSubmitResult(null)
    closeModal()
    resetState()
  }, [resetState, closeModal])

  const updateStudent = useCallback(
    (
      index: number,
      field: 'firstName' | 'lastName' | 'username' | 'gradeId',
      value: string,
    ) => {
      setStudents(prev => {
        const updated = [...prev]
        const current = updated[index]

        if (field === 'gradeId') {
          updated[index] = {
            ...current,
            gradeId: value || null,
            errors: { ...current.errors, gradeId: undefined },
          }
        } else if (field === 'username') {
          updated[index] = {
            ...current,
            username: value,
            usernameEdited: true,
            errors: { ...current.errors, username: undefined },
          }
        } else {
          const patched = {
            ...current,
            [field]: value,
            errors: { ...current.errors, [field]: undefined },
          }
          if (!patched.usernameEdited) {
            patched.username = deriveUsername(
              field === 'firstName' ? value : patched.firstName,
              field === 'lastName' ? value : patched.lastName,
            )
          }
          updated[index] = patched
        }

        if (index === prev.length - 1 && hasData(updated[index])) {
          updated.push(createEmptyStudent())
        }
        return updated
      })
    },
    [],
  )

  const updateStudentGroupIds = useCallback(
    (index: number, value: string[]) => {
      setStudents(prev => {
        const updated = [...prev]
        const current = updated[index]
        updated[index] = {
          ...current,
          groupIds: value,
          errors: { ...current.errors, groupIds: undefined },
        }
        return updated
      })
    },
    [],
  )

  const removeStudent = useCallback((index: number) => {
    setStudents(prev => {
      if (prev.length === 1) return [createEmptyStudent()]
      const updated = prev.filter((_, i) => i !== index)
      if (!updated.some(s => !hasData(s))) {
        updated.push(createEmptyStudent())
      }
      return updated
    })
  }, [])

  const addStudent = useCallback(() => {
    setStudents(prev => [...prev, createEmptyStudent()])
  }, [])

  const clearSubmitResult = useCallback(() => {
    setSubmitResult(null)
  }, [])

  const fillDownGrade = useCallback(() => {
    setStudents(prev => {
      const source = prev.find(s => hasData(s) && s.gradeId)
      if (!source) return prev
      return prev.map(s => {
        if (!hasData(s) || s.gradeId) return s
        return { ...s, gradeId: source.gradeId }
      })
    })
  }, [])

  const fillDownGroup = useCallback(() => {
    setStudents(prev => {
      const source = prev.find(s => hasData(s) && s.groupIds.length > 0)
      if (!source) return prev
      return prev.map(s => {
        if (!hasData(s) || s.groupIds.length > 0) return s
        return { ...s, groupIds: [...source.groupIds] }
      })
    })
  }, [])

  const createGroup = useCallback(async (name: string) => {
    const newGroup = await createGroupQuick(name)
    setGroups(prev => [...prev, { id: newGroup.id, name: newGroup.name }])
    onGroupCreated?.()
  }, [onGroupCreated])

  const submitStudents = useCallback(async () => {
    // Clear previous submit result so stale alerts don't persist
    // alongside new inline validation errors.
    setSubmitResult(null)

    const withData = students.filter(hasData)

    if (withData.length === 0) {
      notifications.show({
        message: t('add_students_modal.no_students_to_add'),
        color: 'red',
        icon: <IconX size={24} />,
      })
      return
    }

    // Check for duplicate usernames across rows
    const usernameCount = new Map<string, number>()
    withData.forEach(s => {
      const u = s.username.trim().toLowerCase()
      if (u) usernameCount.set(u, (usernameCount.get(u) || 0) + 1)
    })

    // Computed outside setStudents (not read from a variable mutated inside
    // the updater) because React may skip running the updater synchronously
    // here — e.g. when a prior setSubmitResult(null) in this same call
    // already scheduled a pending update, such as on a second submit within
    // the same modal session. Reading `valid` right after setStudents would
    // then see its stale initial value instead of the validation result.
    const validatedStudents = students.map(s => {
      if (!hasData(s)) return s
      const errors: StudentRowErrors = {}
      if (!s.firstName.trim()) {
        errors.firstName = t('add_students_modal.field_required')
      }
      if (!s.lastName.trim()) {
        errors.lastName = t('add_students_modal.field_required')
      }
      if (s.username && !hasValidUsernameLength(s.username)) {
        errors.username = t('add_students_modal.username_invalid_length', {
          min: USERNAME_MIN_LENGTH,
          max: USERNAME_MAX_LENGTH,
        })
      } else if (s.username && !USERNAME_REGEX.test(s.username)) {
        errors.username = t('add_students_modal.username_invalid_chars')
      }
      if (s.groupIds.length === 0) {
        errors.groupIds = t('add_students_modal.field_required')
      }
      if (!s.gradeId) {
        errors.gradeId = t('add_students_modal.field_required')
      }
      return {
        ...s,
        errors: Object.keys(errors).length ? errors : undefined,
      }
    })
    setStudents(validatedStudents)

    const valid = validatedStudents.every(s => !s.errors)
    if (!valid) return

    setSavingStudents(true)

    try {
      const result = await createStudentsBatch(
        withData.map(s => ({
          firstName: s.firstName,
          lastName: s.lastName,
          username: s.username || undefined,
          gradeId: s.gradeId,
          groupIds: s.groupIds,
        })),
      )

      setSubmitResult(result)
      setSubmittedGroupIds(collectSubmittedGroupIds(withData))

      // Highlight the name, last name and username fields of the rows that
      // were not added (AR-740), resolved with the same rules used to clear
      // added rows (batch `index` + username fallback) so a kept failed row
      // is always highlighted. When the resolver cannot identify every
      // failed row (null), fall back to the pre-AR-783 behavior and
      // highlight only the rows resolvable by index.
      const failedRowIds =
        resolveFailedRowIds(withData, result.failedStudents) ??
        new Set(
          result.failedStudents
            .map(f => (f.index !== null ? withData[f.index]?.id : undefined))
            .filter((id): id is string => Boolean(id)),
        )
      if (failedRowIds.size > 0) {
        setStudents(prev =>
          prev.map(s =>
            failedRowIds.has(s.id)
              ? { ...s, errors: { firstName: true, lastName: true, username: true } }
              : s,
          ),
        )
      }

      // Clear the rows that were successfully added so a re-submit cannot
      // duplicate them (AR-783). Failed rows keep their data (and the
      // highlights set above) so the user can fix and retry them; when
      // nothing was added, or a failed row cannot be identified, all rows
      // are kept untouched.
      setStudents(prev =>
        removeSubmittedRows(prev, withData, result, createEmptyStudent),
      )

      if (result.failedStudents.length === 0) {
        onStudentsAdded?.()
      } else if (result.addedStudents.length > 0) {
        notifications.show({
          message: t('add_students_modal.partial_success_message', {
            added: result.addedStudents.length,
            failed: result.failedStudents.length,
          }),
          color: 'yellow',
          icon: <IconX size={24} />,
        })
        onStudentsAdded?.()
      } else {
        notifications.show({
          message: t('add_students_modal.all_failed_message'),
          color: 'red',
          icon: <IconX size={24} />,
        })
      }
    } catch (error: unknown) {
      const msg =
        error instanceof Error
          ? error.message
          : t('add_students_modal.error_message')
      notifications.show({
        message: msg,
        color: 'red',
        icon: <IconX size={24} />,
      })
    } finally {
      setSavingStudents(false)
    }
  }, [students, onStudentsAdded, t])

  const value = useMemo(
    () => ({
      opened,
      activeTab,
      groups,
      grades,
      students,
      inviteCode,
      loading,
      loadingGroups,
      savingStudents,
      hasUnsavedChanges,
      showDiscardModal,
      submitResult,
      submittedGroupIds,
      canFillDownGrade,
      canFillDownGroup,
      open,
      close,
      setActiveTab,
      updateStudent,
      updateStudentGroupIds,
      removeStudent,
      addStudent,
      submitStudents,
      setShowDiscardModal,
      confirmDiscard,
      clearSubmitResult,
      fillDownGrade,
      fillDownGroup,
      dismissSuccess,
      createGroup,
    }),
    [
      opened,
      activeTab,
      groups,
      grades,
      students,
      inviteCode,
      loading,
      loadingGroups,
      savingStudents,
      hasUnsavedChanges,
      showDiscardModal,
      submitResult,
      submittedGroupIds,
      canFillDownGrade,
      canFillDownGroup,
      open,
      close,
      updateStudent,
      updateStudentGroupIds,
      removeStudent,
      addStudent,
      submitStudents,
      confirmDiscard,
      clearSubmitResult,
      fillDownGrade,
      fillDownGroup,
      dismissSuccess,
      createGroup,
    ],
  )

  return (
    <Ctx.Provider value={value}>
      {children}
    </Ctx.Provider>
  )
}

export function useAddStudentsModal(): ContextType {
  const ctx = useContext(Ctx)
  if (!ctx) {
    throw new Error(
      'useAddStudentsModal must be used within Provider',
    )
  }
  return ctx
}
